import React, { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { ref } from 'yup';

import { ApproveFrequentPassageRequestButton } from '@shared/frequent-passage-requests/components/ApproveFrequentPassageRequestButton';
import { RedeemFrequentPassageRequestButton } from '@shared/frequent-passage-requests/components/RedeemFrequentPassageRequestButton';
import { RejectFrequentPassageRequestButton } from '@shared/frequent-passage-requests/components/RejectFrequentPassageRequestButton';
import { Tooltip } from '@shared/ui/components/Tooltip';

import Confirm from '../../../base/Confirm';
import ListView from '../../../base/ListView';
import { useDeleteFrequentPassageRequest } from '../../../hooks/api/frequent-passage-requests/useDeleteFrequentPassageRequest';
import { useFrequentPassageStickerTypes } from '../../../hooks/api/frequent-passage-sticker-types/useFrequentPassageStickerTypes';
import {
  CrewMember,
  Event,
  FrequentPassageRequest,
  FrequentPassageStickerType,
  QueryListResult,
  Registration,
} from '../../../hooks/api/utils/types';
import { dateRender, defaultError } from '../../../libs/utils';
import { getRegistrationCrewMembers } from '../../../services/api/registrationCrewMembers';

type RegistrationData = App.Domain.Registration.Data.RegistrationData;

type Props = {
  data: QueryListResult<FrequentPassageRequest>;
  refetch: () => void;
  registration: RegistrationData;
  event: Event;
  filters: Record<string, any>;
  admin: boolean;
  onFiltersChange: (filters: Record<string, any>) => void;
};

export default function FrequentPassageRequestsListView({
  data,
  refetch,
  registration,
  event,
  admin,
  filters,
  onFiltersChange,
}: Props) {
  const { mutate: deleteRequest } = useDeleteFrequentPassageRequest();

  const { data: frequentPassageStickerTypes } = useFrequentPassageStickerTypes(event.id);

  async function handleDelete(frequentPassageRequestId: number) {
    await Confirm('Are you sure you want to delete this frequent passage request? This action can not be undone.', {
      title: 'Delete frequent passage request',
      confirmStyle: 'btn-danger',
      confirmText: 'Delete',
    });

    return deleteRequest({ frequentPassageRequestId });
  }

  const listViewConf = {
    dataSet: data?.data,
    dataMeta: data?.meta,
    columns: [
      {
        key: 'crewMember.user.fullName',
        value: 'Crew Member',
        render: (request: FrequentPassageRequest) => (
          <>
            <strong>{request.crewMember.user?.fullName}</strong>
            <br />
            {request.licensePlate ? <small>{request.licensePlate}</small> : ''}
          </>
        ),
      },
      {
        key: 'frequentPassageStickerType.name',
        value: 'Sticker',
      },
      {
        key: 'status',
        value: 'Status',
        render: (frequentPassageRequest: FrequentPassageRequest) => (
          <StatusBadge frequentPassageRequest={frequentPassageRequest} />
        ),
      },
      {
        key: 'frequentPassageSticker.code',
        value: 'Code',
      },
      {
        key: 'redeemedAt',
        value: 'Redeemed',
        sort: 'redeemed_at',
        render: (frequentPassageRequest: FrequentPassageRequest) =>
          frequentPassageRequest.redeemedAt && dateRender(frequentPassageRequest.redeemedAt),
      },
      {
        key: 'createdAt',
        value: 'Created',
        sort: 'created_at',
        render: (frequentPassageRequest: FrequentPassageRequest) => dateRender(frequentPassageRequest.updatedAt),
      },
    ],
    baseFilters: filters,
    filters: [
      {
        label: 'Status',
        key: 'status',
        icon: 'la-check',
        type: 'select',
        col: '3',
        dataset: [
          {
            key: 'pending',
            value: 'Pending',
          },
          {
            key: 'approved',
            value: 'Approved',
          },
          {
            key: 'rejected',
            value: 'Rejected',
          },
          {
            key: 'redeemed',
            value: 'Redeemed',
          },
        ],
      },
      {
        label: 'Crew Member',
        icon: 'la-user',
        type: 'typeahead',
        col: '3',
        options: (value: string) =>
          getRegistrationCrewMembers(registration.id, {
            'filter[name_like]': value,
            'include': 'user',
          }),
        key: 'crew_member_id',
        labelKey: (item: CrewMember) => `${item.user?.fullName}`,
      },
      {
        label: 'License Plate',
        icon: 'la-car',
        type: 'text',
        key: 'license_plate',
        col: '3',
      },
      {
        label: 'Sticker Type',
        icon: 'la-tag',
        type: 'select',
        key: 'frequent_passage_sticker_type_id',
        col: '5',
        dataset:
          frequentPassageStickerTypes?.data?.map((stickerType: FrequentPassageStickerType) => ({
            key: stickerType.id,
            value: stickerType.name,
          })) || [],
      },
      {
        label: 'Sticker Code',
        icon: 'la-tag',
        type: 'text',
        col: '3',
        key: 'sticker_code',
      },
    ],
    actions: {
      deleteLink: true,
      deleteHideFunction: (item: FrequentPassageRequest) => !item.canDelete || !admin,
      deleteFunction: handleDelete,
      editLink: false,
      // detailsLink: true
      customLink: (item: FrequentPassageRequest) => (
        <RowActions frequentPassageRequest={item} admin={admin} refetch={refetch} />
      ),
    },
    url: '/frequent-passage-requests',
  };

  return (
    <>
      {!data.data && (
        <div className="alert alert-info">
          <Trans>frontend.crew.noRegistrationFrequentPassageRequestsMsg</Trans>
        </div>
      )}
      {data.data && <ListView config={listViewConf} handleFilterUpdate={onFiltersChange} />}
    </>
  );
}

function StatusBadge({ frequentPassageRequest }: { frequentPassageRequest: FrequentPassageRequest }) {
  const statusColors = {
    pending: 'metal',
    rejected: 'warning',
    approved: 'success',
    redeemed: 'info',
  };

  return (
    <Tooltip
      content={
        <>
          {frequentPassageRequest.internalNote && (
            <p>
              <strong className="font-bold">Pick-up note</strong>: {frequentPassageRequest.internalNote}
            </p>
          )}
          {frequentPassageRequest.remark && (
            <p>
              <strong className="font-bold">Remark</strong>: {frequentPassageRequest.remark}
            </p>
          )}
        </>
      }
    >
      <span className={`m-badge m-badge--${statusColors[frequentPassageRequest.status]} m-badge--wide`}>
        {frequentPassageRequest.status}
        {frequentPassageRequest.remark || frequentPassageRequest.internalNote ? '*' : ''}
      </span>
    </Tooltip>
  );
}

function RowActions({
  frequentPassageRequest,
  admin,
  refetch,
}: {
  frequentPassageRequest: FrequentPassageRequest;
  admin: boolean;
  refetch: () => void;
}) {
  let buttons = [];

  if (admin && frequentPassageRequest.canRedeem) {
    buttons.push(
      <RedeemFrequentPassageRequestButton frequentPassageRequest={frequentPassageRequest} onRedeem={refetch} />
    );
  }

  if (frequentPassageRequest.downloadUrl) {
    buttons.push(
      <a
        className="btn btn-outline-primary m-btn btn-sm  m-btn--icon m--margin-left-5"
        href={frequentPassageRequest.downloadUrl}
        download={true}
      >
        <span>
          <i className="la la-download" />
          <span>Download Voucher</span>
        </span>
      </a>
    );
  }

  if (admin && (frequentPassageRequest.status === 'pending' || frequentPassageRequest.status === 'rejected')) {
    buttons.push(
      <ApproveFrequentPassageRequestButton frequentPassageRequest={frequentPassageRequest} onApprove={refetch} />
    );
  }

  if (admin && frequentPassageRequest.canReject) {
    buttons.push(
      <RejectFrequentPassageRequestButton frequentPassageRequest={frequentPassageRequest} onReject={refetch} />
    );
  }

  return <div className="inline-flex flex-row justify-end gap-2">{buttons}</div>;
}
